import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectCanCreateGreenInvoice,
  selectIsDigitalInvoiceActive,
  selectIsAdmin,
  selectHasPermission,
  selectIsGrowIntegrated,
} from "../../store/slices/authSlice";
import {
  useUpdateAppointmentMutation,
  calendarApi,
} from "../../store/services/calendarApi";
import {
  useUpdateTransactionMutation,
  financeApi,
} from "../../store/services/financeApi";
import {
  getTransactionByAppointment,
  TRANSACTION_STATUS,
} from "../../firebase";
import {
  X,
  Check,
  CurrencyCircleDollar,
  Receipt,
} from "@phosphor-icons/react";
import DatePicker from "../ui/DatePicker";
// Grow: ask before recording a manual payment while a Grow link is still active (Phase 5.6)
import useCancelGrowLinkOnManualPayment from "../../hooks/useCancelGrowLinkOnManualPayment";
import InvoiceQuickModal from "../morning/InvoiceQuickModal";
import DigitalInvoiceQuickModal from "../digitalInvoice/DigitalInvoiceQuickModal";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./CalendarPaymentModal.css";
import { getIsraelDateString } from "../../utils/dateUtils";

const PAYMENT_METHODS = [
  { value: "cash", label: "מזומן" },
  { value: "credit", label: "אשראי" },
  { value: "transfer", label: "העברה" },
  { value: "check", label: "צ׳ק" },
  { value: "bit", label: "אפליקציה", subLabel: "ביט/פייבוקס/אחר" },
  { value: "other", label: "אחר" },
];

const STEPS = {
  AMOUNT: "amount",
  DETAILS: "details",
  INVOICE_ASK: "invoice_ask",
};

function CalendarPaymentModal({ isOpen, onClose, appointment }) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isUsingGreenInvoice = useSelector(selectCanCreateGreenInvoice);
  const isDigitalInvoiceActive = useSelector(selectIsDigitalInvoiceActive);
  const isAdmin = useSelector(selectIsAdmin);
  const canCollectForOthers = useSelector(
    selectHasPermission("finance.collectForOthers")
  );

  // Spec E7: a payment always belongs to the appointment's therapist. The
  // linked transaction was created with appointment.therapistId (and
  // updateFinancialTransaction never rewrites it); the `therapistId` passed to
  // the mutations below is the acting user, used only for the activity log.
  const isOtherTherapistAppointment =
    !!appointment?.therapistId && appointment.therapistId !== therapistId;
  // Non-admins need finance.collectForOthers to record a payment for a
  // colleague's appointment (legacy therapists: false → blocked, as today).
  const blockedForOthers =
    isOtherTherapistAppointment && !isAdmin && !canCollectForOthers;

  const [step, setStep] = useState(STEPS.AMOUNT);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentDate, setPaymentDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const growGuard = useCancelGrowLinkOnManualPayment();
  const isGrowIntegrated = useSelector(selectIsGrowIntegrated);
  const [error, setError] = useState(null);

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoicePaidAmount, setInvoicePaidAmount] = useState(null);
  const [invoiceAppointment, setInvoiceAppointment] = useState(null);

  const [updateAppointment] = useUpdateAppointmentMutation();
  const [updateTransaction] = useUpdateTransactionMutation();

  const invalidateAllFinanceTags = (patientId = null) => {
    dispatch(
      calendarApi.util.invalidateTags([
        { type: "Finance", id: "STATS" },
        { type: "Finance", id: "PENDING_PAYMENTS" },
        { type: "Finance", id: "UPCOMING" },
        ...(patientId ? [{ type: "Finance", id: `PATIENT_${patientId}` }] : []),
      ])
    );
    dispatch(
      financeApi.util.invalidateTags([
        { type: "TransactionStats", id: "MONTHLY" },
        { type: "PendingCollection", id: "LIST" },
        { type: "Transactions", id: "EXPECTED" },
        { type: "Transactions", id: "PAID_HISTORY" },
        ...(patientId
          ? [{ type: "Transactions", id: `PATIENT_${patientId}` }]
          : []),
      ])
    );
  };

  const resetState = () => {
    setStep(STEPS.AMOUNT);
    setPaymentAmount("");
    setPaymentMethod("cash");
    setPaymentDate("");
    setIsLoading(false);
    setError(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleAmountConfirm = () => {
    const appointmentDate = new Date(appointment.startTime);
    setPaymentDate(getIsraelDateString(appointmentDate));
    setStep(STEPS.DETAILS);
  };

  const handlePaymentConfirm = async () => {
    if (blockedForOthers) return;
    // Grow guard runs BEFORE any write (appointment + transaction) — only for Grow clinics
    if (isGrowIntegrated) {
      try {
        const existingTx = await getTransactionByAppointment(clinicId, appointment.id);
        if (existingTx?.growLinkId) {
          const proceed = await growGuard.ensureNoActiveLink(existingTx);
          if (!proceed) return;
        }
      } catch (_) {
        /* lookup failure must not block manual bookkeeping */
      }
    }
    setIsLoading(true);
    setError(null);

    const amount = parseFloat(paymentAmount);
    const method = paymentMethod || "cash";
    const date = paymentDate
      ? new Date(paymentDate).toISOString()
      : new Date().toISOString();

    try {
      await updateAppointment({
        clinicId,
        appointmentId: appointment.id,
        appointmentData: {
          isTherapistUpdatedPaymentStatus: true,
          NotifyAboutThisMeetingPaymentStatus: false,
          paymentStatus: "paid",
          paymentAmount: amount,
          actualPaidAmount: amount,
          paymentMethod: method,
          paymentDate: date,
        },
        therapistId,
        therapistName,
        patientName: appointment.patientName,
      }).unwrap();

      try {
        const transaction = await getTransactionByAppointment(
          clinicId,
          appointment.id
        );
        if (transaction) {
          await updateTransaction({
            clinicId,
            transactionId: transaction.id,
            updateData: {
              status: TRANSACTION_STATUS.PAID,
              amount: amount,
              paymentMethod: method,
              actualPaymentDate: date,
            },
            therapistId,
            therapistName,
            patientName: appointment.patientName,
            patientId: appointment.patientId,
          }).unwrap();

          invalidateAllFinanceTags(appointment.patientId);
        }
      } catch (txError) {
        console.error("[CK-3500] Error updating transaction to paid:", {
          errorCode: "CK-3500",
          errorMessage: txError.message,
          appointmentId: appointment.id,
          timestamp: new Date().toISOString(),
        });
      }

      setIsLoading(false);

      if (isUsingGreenInvoice) {
        setStep(STEPS.INVOICE_ASK);
      } else {
        handleClose();
      }
    } catch (err) {
      console.error("[CK-3501] Error updating payment from calendar:", {
        errorCode: "CK-3501",
        errorMessage: err.message,
        appointmentId: appointment.id,
        timestamp: new Date().toISOString(),
      });
      setIsLoading(false);
      setError("שגיאה בעדכון התשלום. נסה שוב.");
    }
  };

  const handleInvoiceYes = () => {
    setInvoiceAppointment({ ...appointment, paymentMethod });
    setInvoicePaidAmount(parseFloat(paymentAmount));
    setShowInvoiceModal(true);
    handleClose();
  };

  const handleInvoiceNo = () => {
    handleClose();
  };

  const handleInvoiceSuccess = async (invoiceData) => {
    if (!invoiceAppointment) return;
    const invoiceRef = {
      documentId: invoiceData.documentId,
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceUrl: invoiceData.invoiceUrl,
      invoiceProvider: invoiceData.invoiceProvider,
      createdAt: new Date().toISOString(),
    };
    try {
      await updateAppointment({
        clinicId,
        appointmentId: invoiceAppointment.id,
        appointmentData: {
          isProducedReceipitForThisMeeting: true,
          RecepitsDocsRefArray: [
            ...(invoiceAppointment.RecepitsDocsRefArray || []),
            invoiceRef,
          ],
        },
        therapistId,
        therapistName,
        patientName: invoiceAppointment.patientName,
      }).unwrap();
    } catch (err) {
      console.error("[CK-3502] Error linking invoice:", {
        errorCode: "CK-3502",
        errorMessage: err.message,
        appointmentId: invoiceAppointment.id,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleInvoiceModalClose = () => {
    setShowInvoiceModal(false);
    setInvoiceAppointment(null);
    setInvoicePaidAmount(null);
  };

  if (!isOpen && !showInvoiceModal) return null;

  const defaultAmount = paymentAmount || appointment?.paymentAmount || "";
  const hasValidAmount = defaultAmount && parseFloat(defaultAmount) > 0;

  const formatAppointmentInfo = () => {
    if (!appointment) return "";
    const date = new Date(appointment.startTime);
    const dateStr = date.toLocaleDateString("he-IL", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
    const timeStr = date.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${appointment.patientName} • ${dateStr} ${timeStr}`;
  };

  return (
    <>
      {isOpen && (
        <div className="cal-payment-overlay" onClick={handleClose}>
          <div
            className="cal-payment-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cal-payment-header">
              <div className="cal-payment-title">
                <CurrencyCircleDollar size={22} weight="duotone" />
                <span>תיעוד תשלום</span>
              </div>
              <button className="cal-payment-close" onClick={handleClose}>
                <X size={18} />
              </button>
            </div>

            <div className="cal-payment-appointment-info">
              {formatAppointmentInfo()}
            </div>

            {error && <div className="cal-payment-error">{error}</div>}
            {blockedForOthers && (
              <div className="cal-payment-error" role="alert">
                אין לך הרשאה לרשום תשלום עבור תור של מטפל/ת אחר/ת
              </div>
            )}

            {step === STEPS.AMOUNT && (
              <div className="cal-payment-step">
                <p className="cal-payment-question">
                  האם התשלום עבור הפגישה טופל?
                </p>
                <div className="cal-payment-amount-row">
                  <span className="cal-payment-currency">₪</span>
                  <input
                    type="number"
                    className="cal-payment-input"
                    placeholder="סכום"
                    value={paymentAmount || defaultAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="cal-payment-actions">
                  <button
                    className="cal-payment-btn confirm"
                    disabled={
                      blockedForOthers ||
                      (!hasValidAmount &&
                        !(paymentAmount && parseFloat(paymentAmount) > 0))
                    }
                    onClick={() => {
                      if (!paymentAmount && defaultAmount) {
                        setPaymentAmount(String(defaultAmount));
                      }
                      handleAmountConfirm();
                    }}
                  >
                    <Check size={18} weight="bold" />
                    <span>המשך</span>
                  </button>
                  <button
                    className="cal-payment-btn cancel"
                    onClick={handleClose}
                  >
                    <X size={18} weight="bold" />
                    <span>ביטול</span>
                  </button>
                </div>
              </div>
            )}

            {step === STEPS.DETAILS && (
              <div className="cal-payment-step">
                <p className="cal-payment-question">פרטי התשלום</p>

                <div className="cal-payment-details-form">
                  <div className="cal-payment-detail-row">
                    <label>סכום</label>
                    <div className="cal-payment-amount-display">
                      ₪{parseFloat(paymentAmount || defaultAmount).toLocaleString("he-IL")}
                    </div>
                  </div>

                  <div className="cal-payment-detail-row">
                    <label>תאריך קבלה</label>
                    <DatePicker
                      className="cal-payment-date-input"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                    />
                  </div>

                  <div className="cal-payment-detail-row">
                    <label>אמצעי תשלום</label>
                    <div className="cal-payment-method-buttons">
                      {PAYMENT_METHODS.map((method) => (
                        <button
                          key={method.value}
                          type="button"
                          className={`cal-payment-method-btn ${
                            paymentMethod === method.value ? "selected" : ""
                          }`}
                          onClick={() => setPaymentMethod(method.value)}
                        >
                          {method.label}
                          {method.subLabel && (
                            <span className="cal-payment-method-sub">
                              {method.subLabel}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="cal-payment-actions">
                  <button
                    className="cal-payment-btn confirm"
                    onClick={handlePaymentConfirm}
                    disabled={isLoading || blockedForOthers}
                  >
                    {isLoading ? (
                      <LoaderLoop size="xs" />
                    ) : (
                      <Check size={18} weight="bold" />
                    )}
                    <span>{isLoading ? "שומר..." : "אישור תשלום"}</span>
                  </button>
                  <button
                    className="cal-payment-btn cancel"
                    onClick={() => setStep(STEPS.AMOUNT)}
                    disabled={isLoading}
                  >
                    <span>חזרה</span>
                  </button>
                </div>
              </div>
            )}

            {step === STEPS.INVOICE_ASK && (
              <div className="cal-payment-step">
                <Receipt size={28} weight="duotone" className="cal-payment-invoice-icon" />
                <p className="cal-payment-question">להפיק חשבונית?</p>
                <div className="cal-payment-actions">
                  <button
                    className="cal-payment-btn confirm"
                    onClick={handleInvoiceYes}
                  >
                    <Check size={18} weight="bold" />
                    <span>כן</span>
                  </button>
                  <button
                    className="cal-payment-btn cancel"
                    onClick={handleInvoiceNo}
                  >
                    <X size={18} weight="bold" />
                    <span>לא</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isDigitalInvoiceActive ? (
        <DigitalInvoiceQuickModal
          isOpen={showInvoiceModal}
          onClose={handleInvoiceModalClose}
          appointment={invoiceAppointment}
          paidAmount={invoicePaidAmount}
          onSuccess={handleInvoiceSuccess}
        />
      ) : (
        <InvoiceQuickModal
          isOpen={showInvoiceModal}
          onClose={handleInvoiceModalClose}
          appointment={invoiceAppointment}
          paidAmount={invoicePaidAmount}
          onSuccess={handleInvoiceSuccess}
        />
      )}
      {growGuard.prompt}
    </>
  );
}

export default CalendarPaymentModal;
